import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Per-set machinery behind the global unread tracker: one instance manages
 * one set of rooms (e.g. COACHING chat rooms, or the viewer's assigned
 * support tickets). Extracted from RoomsUnreadProvider so a second room set
 * doesn't duplicate the resync / stagger / epoch plumbing.
 *
 * `fetchRooms()` must resolve to `[{ id, unreadCount, summary }]` where
 * `unreadCount` is a number (server-authoritative — overwrites local state)
 * or `null` (unknown — the current local count is preserved, defaulting to
 * 0). The null path is how support tickets degrade gracefully when the
 * backend can't report per-room unread counts: badges then run on realtime
 * increments alone, so unread accrued while the app was closed stays
 * invisible until the room is opened or the count source starts working.
 *
 * Rooms absent from a fetch drop out of the map entirely — that is how
 * resolved/reassigned tickets (or archived rooms) stop counting.
 *
 * A change of `fetchRooms` identity triggers an immediate resync, so callers
 * can encode scope changes (e.g. the staff probe resolving) in its deps.
 *
 * @param {Object} opts
 * @param {() => Promise<Array<{id: string, unreadCount: number|null, summary: Object}>>} opts.fetchRooms
 * @param {string|null} opts.activeRoomId - Room whose thread is open (count clears on navigation).
 * @param {number} opts.connectionEpoch - Bumps on websocket (re)connect; triggers a resync.
 * @param {({roomId, msg, roomSummary}) => void} opts.onIncoming - Fired per inbound message in a non-active room, after the increment.
 * @param {number} opts.resyncIntervalMs
 * @param {number} opts.batchSize
 * @param {number} opts.batchIntervalMs
 */
export function useUnreadRoomSet({
  fetchRooms,
  activeRoomId,
  connectionEpoch,
  onIncoming,
  resyncIntervalMs,
  batchSize,
  batchIntervalMs,
}) {
  const [roomIds, setRoomIds] = useState([]);
  const [visibleRoomIds, setVisibleRoomIds] = useState([]);
  const [unreadByRoom, setUnreadByRoom] = useState(new Map());
  // Bumped after every successful fetch — lets list UIs (e.g. TicketList)
  // refetch in step with this set's resync cadence instead of polling twice.
  const [resyncVersion, setResyncVersion] = useState(0);
  // Room summaries (client name etc.) for the notify layer — a ref, not
  // state: name lookups shouldn't re-render the app tree.
  const roomSummariesRef = useRef(new Map());

  // Ref'd so handleMessage stays identity-stable — it feeds the watchers'
  // memoized handler objects.
  const onIncomingRef = useRef(onIncoming);
  onIncomingRef.current = onIncoming;

  // Resyncs can overlap (interval tick + instant requestSupportResync + epoch
  // bump); drop any response superseded by a newer request so a slow, older
  // fetch can't overwrite fresher state.
  const resyncSeqRef = useRef(0);

  const resync = useCallback(async () => {
    if (!localStorage.getItem("access_token")) {
      return;
    }
    const seq = ++resyncSeqRef.current;
    try {
      const rooms = await fetchRooms();
      if (seq !== resyncSeqRef.current || !Array.isArray(rooms)) {
        return;
      }
      // Keep the array reference stable when the room set hasn't changed —
      // a new reference re-runs the batching effect below, which unmounts
      // every watcher past the first batch and drops realtime events during
      // the re-stagger window. That would repeat on every resync.
      setRoomIds((prev) => {
        const next = rooms.map((r) => r.id);
        const unchanged =
          prev.length === next.length && prev.every((id, i) => id === next[i]);
        return unchanged ? prev : next;
      });
      setUnreadByRoom((prev) => {
        const next = new Map();
        for (const r of rooms) {
          next.set(
            r.id,
            r.unreadCount == null ? prev.get(r.id) || 0 : Number(r.unreadCount) || 0
          );
        }
        return next;
      });
      roomSummariesRef.current = new Map(rooms.map((r) => [r.id, r.summary]));
      setResyncVersion((v) => v + 1);
    } catch (err) {
      // Backend unreachable — keep last-known state, never crash.
      console.error("useUnreadRoomSet: failed to fetch rooms", err);
    }
  }, [fetchRooms]);

  // Initial load — also re-fires when `fetchRooms` identity changes (scope
  // change, e.g. the staff probe resolving to true).
  useEffect(() => {
    resync();
  }, [resync]);

  // On every websocket (re)connect, events may have been missed while down:
  // unread counts drift. Resync immediately instead of waiting the next tick.
  const epochRef = useRef(connectionEpoch);
  useEffect(() => {
    if (connectionEpoch === epochRef.current) {
      return;
    }
    epochRef.current = connectionEpoch;
    resync();
  }, [connectionEpoch, resync]);

  useEffect(() => {
    const id = setInterval(resync, resyncIntervalMs);
    return () => clearInterval(id);
  }, [resync, resyncIntervalMs]);

  // Stagger subscribing in batches instead of mounting every room's watcher
  // at once, so app boot doesn't fire N concurrent token-fetch requests.
  useEffect(() => {
    if (roomIds.length === 0) {
      setVisibleRoomIds([]);
      return undefined;
    }
    setVisibleRoomIds(roomIds.slice(0, batchSize));
    let i = batchSize;
    const timer = setInterval(() => {
      if (i >= roomIds.length) {
        clearInterval(timer);
        return;
      }
      setVisibleRoomIds(roomIds.slice(0, i + batchSize));
      i += batchSize;
    }, batchIntervalMs);
    return () => clearInterval(timer);
  }, [roomIds, batchSize, batchIntervalMs]);

  const handleMessage = useCallback((roomId, msg) => {
    setUnreadByRoom((prev) => {
      const next = new Map(prev);
      next.set(roomId, (next.get(roomId) || 0) + 1);
      return next;
    });
    onIncomingRef.current?.({
      roomId,
      msg,
      roomSummary: roomSummariesRef.current.get(roomId) || null,
    });
  }, []);

  const handleReadReset = useCallback((roomId) => {
    setUnreadByRoom((prev) => {
      if (!prev.get(roomId)) {
        return prev;
      }
      const next = new Map(prev);
      next.set(roomId, 0);
      return next;
    });
  }, []);

  // Opening a room's thread is the most reliable "this is now read" signal —
  // Thread.jsx marks it read (POST .../read) almost immediately after its
  // messages load. Clear the badge on navigation itself rather than waiting
  // on a `read` realtime event being echoed back to the viewer's own
  // subscription, which this codebase has no confirmed guarantee of.
  useEffect(() => {
    if (activeRoomId) {
      handleReadReset(activeRoomId);
    }
  }, [activeRoomId, handleReadReset]);

  const total = useMemo(
    () => Array.from(unreadByRoom.values()).reduce((sum, count) => sum + count, 0),
    [unreadByRoom]
  );

  const getSummary = useCallback(
    (roomId) => roomSummariesRef.current.get(roomId) || null,
    []
  );

  return {
    unreadByRoom,
    total,
    visibleRoomIds,
    resync,
    resyncVersion,
    handleMessage,
    handleReadReset,
    getSummary,
  };
}
